import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatDateTime, formatNumber } from "../../lib/format";

export interface ChartPoint {
  time: number;
  value: number;
}

interface TimeSeriesChartProps {
  points: ChartPoint[];
  color?: string;
  height?: number;
  /** Değer biçimlendirici (ör. yüzde, byte). */
  formatValue?: (value: number) => string;
  /** X ekseni biçimlendirici. */
  formatTime?: (time: number) => string;
  ariaLabel: string;
}

const defaultFormatTime = (time: number): string => {
  const date = new Date(time);
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

/**
 * Recharts tabanlı zaman serisi grafiği. Koyu/açık tema için eksen ve
 * ızgara renkleri CSS değişkenlerinden gelir.
 */
export function TimeSeriesChart({
  points,
  color = "var(--chart-1)",
  height = 220,
  formatValue = (value) => formatNumber(value, 1),
  formatTime = defaultFormatTime,
  ariaLabel,
}: TimeSeriesChartProps) {
  return (
    <div style={{ height }} role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid
            stroke="var(--border)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="time"
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(value: number) => formatTime(Number(value))}
            stroke="var(--muted-foreground)"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: "var(--border)" }}
          />
          <YAxis
            stroke="var(--muted-foreground)"
            tick={{ fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={44}
            tickFormatter={(value: number) => formatNumber(Number(value), 0)}
          />
          <Tooltip
            labelFormatter={(label) => formatDateTime(new Date(Number(label)).toISOString())}
            formatter={(value) => [formatValue(Number(value)), ""]}
            contentStyle={{
              backgroundColor: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: "0.5rem",
              color: "var(--foreground)",
              fontSize: "12px",
            }}
            labelStyle={{ color: "var(--muted-foreground)" }}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2}
            fill="url(#chartFill)"
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Renk paleti (CSS değişkenleri index.css içinde tanımlı). */
export const CHART_COLORS = {
  cpu: "var(--chart-1)",
  memory: "var(--chart-2)",
  disk: "var(--chart-3)",
  network: "var(--chart-4)",
  other: "var(--chart-5)",
} as const;